import { Link } from "react-router-dom";
import { MapPin, Clock, DollarSign } from "lucide-react";
import { getFullImageUrl } from "../../../utils/imageHandler";
import { Avatar, AvatarFallback } from "../avatar";
import { Badge } from "../badge";
import { getJobPath, isJobExpired } from "../../../utils/jobSlug";
import { formatJobSalary, getJobCardSnippet } from "../../../utils/jobFormat";
import { glassCardHover } from "../../../utils/glassStyles";

const JobCard = ({ job }) => {
  const expired = isJobExpired(job);
  const jobPath = getJobPath(job);
  const salary = formatJobSalary(job);
  const snippet = getJobCardSnippet(job, 85);

  return (
    <div className={`relative p-4 sm:p-5 rounded-2xl ${glassCardHover} hover:-translate-y-0.5 flex flex-col h-full overflow-hidden`}>
      {expired && (
        <Badge className="absolute top-2.5 right-2.5 z-10 text-[10px] px-2 py-0.5 h-5 bg-amber-500/90 text-white border-0 shadow-sm font-semibold">
          Expired
        </Badge>
      )}

      <div className="flex items-start gap-3 mb-2 pr-14">
        <Avatar className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl border border-white/40 bg-white/70 dark:bg-white/10 shadow-sm overflow-hidden shrink-0">
          {(job.companyLogo || job.recruiter?.logoUrl) && (
            <img
              src={getFullImageUrl(job.companyLogo || job.recruiter?.logoUrl)}
              alt={job.companyName}
              className="w-full h-full object-contain p-1"
            />
          )}
          <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-black text-sm uppercase">
            {job.companyName?.[0] || job.recruiter?.companyName?.[0] || "J"}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm sm:text-base font-semibold text-foreground hover:text-primary transition-colors line-clamp-2 leading-snug">
            <Link to={jobPath}>{job.title}</Link>
          </h3>
          <p className="text-muted-foreground text-xs mt-0.5 truncate">
            {job.companyName || job.recruiter?.companyName || "Unknown Company"}
          </p>
        </div>
      </div>

            {snippet && (
              <p className="text-[11px] sm:text-xs text-muted-foreground line-clamp-2 leading-relaxed mb-2.5 min-h-[2.5rem]">
                {snippet}
              </p>
            )}

      <div className="space-y-1.5 text-muted-foreground text-[11px] sm:text-xs mb-3 flex-1">
        {job.location && (
          <p className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-primary/70 shrink-0" />
            <span className="truncate">{job.location}</span>
          </p>
        )}
        {salary !== "Competitive" && (
          <p className="flex items-center gap-1.5">
            <DollarSign className="w-3.5 h-3.5 text-accent shrink-0" />
            <span className="truncate">{salary}</span>
          </p>
        )}
        {job.deadline && (
          <p className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-orange-500 shrink-0" />
            <span>Deadline: {new Date(job.deadline).toLocaleDateString()}</span>
          </p>
        )}
      </div>

      {job.tags && job.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {job.tags.slice(0, 3).map((tag, i) => (
            <span
              key={i}
              className="bg-primary/10 text-primary border border-primary/20 text-[10px] font-medium px-2 py-0.5 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      <Link
        to={jobPath}
        className="inline-block w-full text-center bg-primary/90 hover:bg-primary text-primary-foreground text-sm font-medium px-4 py-2.5 rounded-xl transition-colors shadow-md shadow-primary/20 mt-auto"
      >
        View Details
      </Link>
    </div>
  );
};

export default JobCard;
